import { useCallback, useEffect, useMemo } from "react";

import { useSetState } from "~/hooks/useSetState";
import { useRect } from "~/hooks/useRect";
import { getPointerDistance } from "~/utils/dom";

import { useMediaPlayerContext } from "./context";
import { MediaChapters } from "./MediaChapters";
import { MediaProgressGroup } from "./MediaProgressGroup";
import { MediaPlayhead } from "./MediaPlayhead";

import type {
  MouseEvent as ReactMouseEvent,
  TouchEvent as ReactTouchEvent,
} from "react";

export interface MediaProgressState {
  isActive: boolean;
  isSeeking: boolean;
  pointerValue: number | null;
  shouldEase: boolean;
}

export function MediaProgress() {
  const [ref, DOMRect] = useRect<HTMLDivElement>();

  const {
    chapters,
    currentTime,
    duration,
    scrub,
    seek,
    setSeeking,
    setSeekingTime,
    seeking,
    seekingTime,
  } = useMediaPlayerContext();

  const [state, setProgressState] = useSetState<MediaProgressState>({
    isActive: false,
    isSeeking: false,
    pointerValue: null,
    shouldEase: true,
  });

  const setState = useCallback(
    (newState: any) => {
      setProgressState(newState);
      if (newState.isSeeking !== undefined) {
        setSeeking(newState.isSeeking);
      }
      if (newState.pointerValue !== undefined) {
        setSeekingTime(newState.pointerValue * duration);
      }
    },
    [setProgressState, setSeeking, setSeekingTime, duration],
  );

  // Computed values
  const playheadValue =
    seeking && state.pointerValue !== null
      ? state.pointerValue
      : currentTime / duration;
  const playheadDistance = (playheadValue ?? 0) * (DOMRect?.width ?? 1);

  const seekToPointerTime = useCallback(() => {
    if (state.pointerValue === null) return;
    setState({ isSeeking: false, isActive: false });
    seek?.(seekingTime);
    requestAnimationFrame(() => {
      setState({ shouldEase: true });
    });
  }, [state.pointerValue, setState, seek, seekingTime]);

  const handleMouseMoveDocument = useCallback(
    (event: MouseEventInit) => {
      if (!ref.current) return;

      const { x } = getPointerDistance(
        ref.current,
        event as unknown as ReactMouseEvent,
      );

      setState({ pointerValue: x, isActive: true });
    },
    [ref, setState],
  );

  const handleMouseUpDocument = useCallback(() => {
    seekToPointerTime();
  }, [seekToPointerTime]);

  const handleMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLDivElement> | ReactTouchEvent<HTMLDivElement>) => {
      if ("button" in e && e.button !== 0) return;

      setState({
        isSeeking: true,
        shouldEase: false,
      });
    },
    [setState],
  );

  const handleMouseMove = useCallback(
    (
      event: ReactMouseEvent<HTMLDivElement> | ReactTouchEvent<HTMLDivElement>,
    ) => {
      const { x } = getPointerDistance(event.currentTarget, event);
      setState({ pointerValue: x, isActive: true });
    },
    [setState],
  );

  const handleMouseUp = useCallback(() => {
    seekToPointerTime();
  }, [seekToPointerTime]);

  const handleMouseOut = useCallback(() => {
    if (!seeking) {
      setState({ isActive: false, pointerValue: null });
    }
  }, [setState, seeking]);

  const renderedTimeline = useMemo(() => {
    if (chapters) {
      return (
        <MediaChapters
          playheadValue={playheadValue}
          pointerValue={state.pointerValue ?? 0}
        />
      );
    }

    return (
      <MediaProgressGroup
        playheadValue={playheadValue}
        pointerValue={state.pointerValue ?? 0}
      />
    );
  }, [chapters, playheadValue, state.pointerValue]);

  // Document events
  useEffect(() => {
    if (seeking) {
      document.addEventListener("mousemove", handleMouseMoveDocument);
      document.addEventListener("mouseup", handleMouseUpDocument);
    } else {
      document.removeEventListener("mousemove", handleMouseMoveDocument);
      document.removeEventListener("mouseup", handleMouseUpDocument);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMoveDocument);
      document.removeEventListener("mouseup", handleMouseUpDocument);
    };
  }, [handleMouseMoveDocument, handleMouseUpDocument, seeking]);

  useEffect(() => {
    if (seeking && state.pointerValue !== null) {
      scrub(Math.round(seekingTime));
    }
  }, [duration, scrub, seeking, state.pointerValue, seekingTime]);

  return (
    <div ref={ref} className="MediaProgress">
      <div
        className="MediaProgress__touch-area"
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        onMouseMove={handleMouseMove}
        onTouchMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        onMouseOut={handleMouseOut}
      />

      {renderedTimeline}

      <MediaPlayhead
        distance={playheadDistance}
        disableMotion={!state.shouldEase}
        isActive={state.isActive}
      />
    </div>
  );
}
